import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/audit")({
	component: AdminAudit,
});

const adminClient = createClient(AdminService, transport);

function actionVariant(
	action: string,
): "default" | "secondary" | "destructive" | "outline" {
	if (action.includes("suspend")) return "destructive";
	if (action.includes("create")) return "default";
	if (action.includes("update")) return "secondary";
	return "outline";
}

function AdminAudit() {
	const { t } = useTranslation("admin");
	useBreadcrumbs([
		{ label: t("admin_root"), href: "/admin" },
		{ label: t("audit_title") },
	]);
	const [targetType, setTargetType] = useState("all");
	const [targetId, setTargetId] = useState("");

	const { data, isLoading } = useQuery({
		queryKey: ["admin", "audit", targetType, targetId],
		queryFn: () =>
			adminClient.listAuditLog({
				targetType: targetType === "all" ? "" : targetType,
				targetId,
				pageSize: 50,
			}),
	});

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Select value={targetType} onValueChange={setTargetType}>
					<SelectTrigger className="w-[150px]">
						<SelectValue placeholder="Target type" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">{t("audit_target_all")}</SelectItem>
						<SelectItem value="workspace">
							{t("audit_target_workspace")}
						</SelectItem>
						<SelectItem value="user">{t("audit_target_user")}</SelectItem>
						<SelectItem value="platform">
							{t("audit_target_platform")}
						</SelectItem>
					</SelectContent>
				</Select>
				<Input
					placeholder={t("audit_filter_id_placeholder")}
					value={targetId}
					onChange={(e) => setTargetId(e.target.value)}
					className="max-w-xs"
				/>
			</div>

			<div className="rounded-lg border overflow-hidden">
				<Table>
					<TableHeader>
						<TableRow className="bg-muted/30">
							<TableHead>{t("audit_col_time")}</TableHead>
							<TableHead>{t("audit_col_admin")}</TableHead>
							<TableHead>{t("audit_col_action")}</TableHead>
							<TableHead>{t("audit_col_target")}</TableHead>
							<TableHead>{t("audit_col_details")}</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{isLoading ? (
							Array.from({ length: 5 }).map((_, i) => (
								<TableRow key={i} className="hover:bg-muted/50">
									<TableCell>
										<Skeleton className="h-4 w-24" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-32" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-20" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-28" />
									</TableCell>
									<TableCell>
										<Skeleton className="h-4 w-40" />
									</TableCell>
								</TableRow>
							))
						) : data?.entries.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={5}
									className="text-center text-muted-foreground"
								>
									{t("audit_empty")}
								</TableCell>
							</TableRow>
						) : (
							data?.entries.map((entry) => (
								<TableRow key={entry.id} className="hover:bg-muted/50">
									<TableCell className="text-sm text-muted-foreground">
										{entry.createdAt
											? new Date(
													Number(entry.createdAt.seconds) * 1000,
												).toLocaleString()
											: "—"}
									</TableCell>
									<TableCell className="font-medium">
										{entry.adminName}
									</TableCell>
									<TableCell>
										<Badge variant={actionVariant(entry.action)}>
											{entry.action}
										</Badge>
									</TableCell>
									<TableCell>
										<div className="text-sm">
											<span className="text-muted-foreground">
												{entry.targetType}
											</span>
											{entry.targetId && (
												<span className="ml-1 font-mono text-xs">
													{entry.targetId.slice(0, 8)}...
												</span>
											)}
										</div>
									</TableCell>
									<TableCell>
										{entry.details && (
											<pre className="text-xs text-muted-foreground max-w-xs truncate">
												{JSON.stringify(entry.details)}
											</pre>
										)}
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
		</div>
	);
}
